import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as reportsApi from "./api";
import type { ExportFormat, ReportFilters, ReportName } from "./api";

export function useOverview(filters: ReportFilters = {}) {
  return useQuery({
    queryKey: ["overview", filters],
    queryFn: () => reportsApi.getOverview(filters),
  });
}

export function useReport(name: ReportName, filters: ReportFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ["report", name, filters],
    queryFn: () => reportsApi.getReport(name, filters),
    enabled,
  });
}

export function useExportReport() {
  return useMutation({
    mutationFn: ({
      name,
      format,
      filters,
      cols,
    }: {
      name: ReportName;
      format: ExportFormat;
      filters?: ReportFilters;
      cols?: number[];
    }) => reportsApi.exportReport(name, format, filters ?? {}, cols),
  });
}

export function useBillingRates(enabled = true) {
  return useQuery({
    queryKey: ["billing-rates"],
    queryFn: reportsApi.listBillingRates,
    enabled,
  });
}

function useInvalidateRates() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["billing-rates"] });
}

export function useCreateBillingRate() {
  const inv = useInvalidateRates();
  return useMutation({ mutationFn: reportsApi.createBillingRate, onSuccess: inv });
}
export function useUpdateBillingRate() {
  const inv = useInvalidateRates();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { label?: string; hourly_rate?: string } }) =>
      reportsApi.updateBillingRate(id, body),
    onSuccess: inv,
  });
}
export function useDeleteBillingRate() {
  const inv = useInvalidateRates();
  return useMutation({ mutationFn: reportsApi.deleteBillingRate, onSuccess: inv });
}
